import React from 'react';
import './CampaignBuilder.css';
import AdvancedCampaignRules from './AdvancedCampaignRules';
import OfferLivePreview from './OfferLivePreview';

const pad2 = (value) => String(value).padStart(2, '0');

const formatLocalDateValue = (date) => (
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
);

const formatLocalTimeValue = (date) => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;

const addMinutesToTime = (timeValue, minutesToAdd) => {
  const [hours, minutes] = String(timeValue || '').split(':').map((part) => Number(part));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return '';
  }
  const totalMinutes = (hours * 60) + minutes + Number(minutesToAdd || 0);
  if (totalMinutes < 0 || totalMinutes >= 24 * 60) {
    return '';
  }
  const nextHours = Math.floor(totalMinutes / 60);
  const nextMinutes = totalMinutes % 60;
  return `${pad2(nextHours)}:${pad2(nextMinutes)}`;
};

const roundUpMinutes = (date, stepMinutes) => {
  const copy = new Date(date.getTime());
  const minutes = copy.getMinutes();
  const next = Math.ceil(minutes / stepMinutes) * stepMinutes;
  copy.setSeconds(0, 0);
  if (next >= 60) {
    copy.setHours(copy.getHours() + 1);
    copy.setMinutes(0);
  } else {
    copy.setMinutes(next);
  }
  return copy;
};

const parseIsoLocal = (value) => {
  const raw = String(value || '').trim();
  const text = raw.includes('T') ? raw : raw.replace(' ', 'T');
  if (!text) {
    return { date: '', time: '' };
  }
  const [datePart, timePart] = text.split('T');
  if (!datePart || !timePart) {
    return { date: '', time: '' };
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return { date: '', time: '' };
  }
  const match = timePart.match(/^(\d{1,2}):(\d{1,2})/);
  if (!match) {
    return { date: datePart, time: '' };
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (Number.isNaN(hour) || Number.isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return { date: datePart, time: '' };
  }
  return { date: datePart, time: `${pad2(hour)}:${pad2(minute)}` };
};

const composeIsoLocal = (datePart, timePart) => {
  if (!datePart || !timePart) {
    return '';
  }
  return `${datePart}T${timePart}`;
};

const CampaignBuilder = ({
  selectedChannels,
  selectedType,
  setSelectedType,
  channelOptions,
  channelLabels,
  offerTypeOptions,
  toggleChannel,
  title,
  onTitleChange,
  discount,
  onDiscountChange,
  appliesOn,
  onAppliesOnChange,
  appliesCategories,
  onAppliesCategoriesChange,
  storeServices,
  isLoadingServices,
  minCartValue,
  onMinCartValueChange,
  redemptionLimitType,
  onRedemptionLimitTypeChange,
  redemptionLimitValue,
  onRedemptionLimitValueChange,
  couponCode,
  onCouponCodeChange,
  maxDiscountCap,
  onMaxDiscountCapChange,
  audience,
  onAudienceChange,
  validDaysLabel,
  onValidDaysLabelChange,
  terms,
  onTermsChange,
  validityFrom,
  onValidityFromChange,
  validityUntil,
  onValidityUntilChange,
  advanced,
  onAdvancedChange,
  onSaveDraft,
  onPublish,
  isSubmitting
}) => {
  const now = new Date();
  const todayValue = formatLocalDateValue(now);
  const maxDate = new Date(now.getTime());
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  const maxDateValue = formatLocalDateValue(maxDate);
  const defaultFromTime = formatLocalTimeValue(roundUpMinutes(now, 15));
  const defaultUntilTime = formatLocalTimeValue(roundUpMinutes(new Date(now.getTime() + 60 * 60 * 1000), 15));

  const fromParts = parseIsoLocal(validityFrom);
  const untilParts = parseIsoLocal(validityUntil);

  const [discountCategory, setDiscountCategory] = React.useState('NONE');
  const [percentOff, setPercentOff] = React.useState('');
  const [flatOff, setFlatOff] = React.useState('');
  const [buyQty, setBuyQty] = React.useState('');
  const [getQty, setGetQty] = React.useState('');
  const [spendAmount, setSpendAmount] = React.useState('');
  const [getAmount, setGetAmount] = React.useState('');
  const [newTerm, setNewTerm] = React.useState('');

  // Wizard: one logical group per step (keeps the modal uncluttered).
  // Order mirrors how a merchant thinks: name it → define the deal & its caps →
  // pick who & when → review and publish. Limits live WITH the offer (not publish).
  const STEPS = ['Basics', 'Offer & limits', 'Who & when', 'Publish'];
  const LAST_STEP = STEPS.length - 1;
  const [step, setStep] = React.useState(0);

  // Valid days are stored as a comma-joined label so the backend contract stays a string.
  const DAY_OPTIONS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const selectedValidDays = String(validDaysLabel || '')
    .split(',')
    .map((day) => day.trim())
    .filter(Boolean);
  const toggleValidDay = (day) => {
    const current = new Set(selectedValidDays);
    if (current.has(day)) {
      current.delete(day);
    } else {
      current.add(day);
    }
    onValidDaysLabelChange?.(DAY_OPTIONS.filter((option) => current.has(option)).join(', '));
  };

  // Coupon codes are generated by us — customers never type their own.
  const COUPON_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const generateCouponCode = () => {
    const base = String(title || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 4) || 'OFFER';
    let suffix = '';
    for (let i = 0; i < 6; i += 1) {
      suffix += COUPON_ALPHABET.charAt(Math.floor(Math.random() * COUPON_ALPHABET.length));
    }
    onCouponCodeChange?.(`${base}${suffix}`);
  };

  // T&C is edited one item at a time; persisted as a newline-joined string.
  const termsList = String(terms || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const addTerm = () => {
    const value = String(newTerm || '').trim();
    if (!value) {
      return;
    }
    onTermsChange?.([...termsList, value].join('\n'));
    setNewTerm('');
  };
  const removeTerm = (index) => {
    onTermsChange?.(termsList.filter((_, current) => current !== index).join('\n'));
  };

  // Service targeting picks live services straight from the store's service menu.
  const selectedServiceNames = String(appliesCategories || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);
  const allServiceNames = (Array.isArray(storeServices) ? storeServices : [])
    .filter((service) => String(service?.catalogStatus || 'ACTIVE').toUpperCase() !== 'INACTIVE')
    .map((service) => String(service?.serviceName || '').trim())
    .filter(Boolean);
  // De-duplicate while preserving menu order, then drop anything already selected.
  const availableServiceNames = Array.from(new Set(allServiceNames))
    .filter((serviceName) => !selectedServiceNames.includes(serviceName));
  const hasServiceMenu = allServiceNames.length > 0;
  const addService = (serviceName) => {
    const value = String(serviceName || '').trim();
    if (!value || selectedServiceNames.includes(value)) {
      return;
    }
    onAppliesCategoriesChange?.([...selectedServiceNames, value].join(', '));
  };
  const removeService = (serviceName) => {
    onAppliesCategoriesChange?.(
      selectedServiceNames.filter((name) => name !== serviceName).join(', ')
    );
  };

  const normalizedLimitType = String(redemptionLimitType || 'none');
  const normalizedLimitValue = String(redemptionLimitValue || '').trim();
  const requiresRedemptionValue = normalizedLimitType === 'per_user' || normalizedLimitType === 'global';
  const redemptionValueOk = !requiresRedemptionValue || (normalizedLimitValue && Number(normalizedLimitValue) > 0);
  const hasTitle = Boolean(String(title || '').trim());
  const hasChannels = selectedChannels && selectedChannels.size > 0;

  const appliesOnKey = String(appliesOn || '').trim();
  const appliesOnOk = (() => {
    if (!appliesOnKey) {
      return false;
    }
    if (appliesOnKey === 'CATEGORIES') {
      return Boolean(String(appliesCategories || '').trim());
    }
    if (appliesOnKey === 'MIN_CART') {
      return Boolean(String(minCartValue || '').trim()) && Number(minCartValue) > 0;
    }
    return true;
  })();

  const hasValidityFrom = Boolean(fromParts.date) && Boolean(fromParts.time);
  const hasValidityUntil = Boolean(untilParts.date) && Boolean(untilParts.time);

  const discountCategoryKey = String(discountCategory || '').trim();
  const discountOk = (() => {
    const adv = advanced || {};
    switch (discountCategoryKey) {
      case 'NONE':
        return true;
      case 'PERCENT':
        return Number(percentOff) > 0;
      case 'FLAT':
        return Number(flatOff) > 0;
      case 'BUY_GET':
        return Number(buyQty) > 0 && Number(getQty) > 0;
      case 'BILL_VALUE':
        return Number(spendAmount) > 0 && Number(getAmount) > 0;
      case 'TIERED':
        return (Array.isArray(adv.tiers) ? adv.tiers : []).some((t) => Number(t.value) > 0);
      case 'FLAT_PRICE':
        return Number(adv.flatPriceValue) > 0;
      case 'FREE_ITEM':
        return Boolean(String(adv.freeItemLabel || '').trim());
      default:
        return false;
    }
  })();

  const canSubmit = (
    hasTitle
    && hasChannels
    && discountOk
    && appliesOnOk
    && hasValidityFrom
    && hasValidityUntil
    && redemptionValueOk
    && !isSubmitting
  );

  const compactChannelLabel = (value) => {
    const text = String(value || '');
    if (!text) return '';
    return text
      .replace('Home feed spotlight', 'Home Feed')
      .replace('Away', 'Home Feed');
  };

  const selectedChannelsLabel = (
    [...selectedChannels]
      .map((channel) => compactChannelLabel(channelLabels[channel]) || channel)
      .filter(Boolean)
      .join(' • ')
    || 'None'
  );

  const titleGroups = [
    {
      label: 'Seasonal & Events',
      options: [
        'End of Season Sale',
        'Festive Special',
        'Weekend Sale',
        'Midweek Special',
        'New Arrivals Launch',
        'Limited Time Offer'
      ]
    },
    {
      label: 'Discount & Savings',
      options: [
        'Flat Discount Deal',
        'Percentage Off Sale',
        'Spend & Save',
        'Bundle & Save',
        'Clearance Sale',
        'Members Only Offer'
      ]
    },
    {
      label: 'Footfall & In-Store',
      options: [
        'In-Store Exclusive',
        'In-Queue Add-on Deal',
        'Happy Hours Deal',
        'Today Only Offer',
        'Flash Deal'
      ]
    },
    {
      label: 'Loyalty & Retention',
      options: [
        'Loyalty Bonus',
        'Repeat Customer Reward',
        'Welcome Offer',
        'VIP Exclusive',
        'Referral Reward'
      ]
    }
  ];

  const compareIsoLocal = (left, right) => {
    if (!left || !right) {
      return 0;
    }
    return left.localeCompare(right);
  };

  const minUntilIso = fromParts.date && fromParts.time ? composeIsoLocal(fromParts.date, fromParts.time) : '';
  const minFromDate = todayValue;
  const minFromTime = (() => {
    if (!fromParts.date) {
      return '00:00';
    }
    if (fromParts.date !== todayValue) {
      return '00:00';
    }
    return defaultFromTime;
  })();
  const minUntilDate = (() => {
    if (!fromParts.date) {
      return todayValue;
    }
    return fromParts.date > todayValue ? fromParts.date : todayValue;
  })();
  const computedMinUntilTime = (() => {
    if (!untilParts.date) {
      return '00:00';
    }
    if (untilParts.date === fromParts.date && fromParts.time) {
      return addMinutesToTime(fromParts.time, 15) || fromParts.time;
    }
    if ((minUntilDate === todayValue || !minUntilIso) && untilParts.date === todayValue) {
      return defaultFromTime;
    }
    return '00:00';
  })();
  const minUntilTime = computedMinUntilTime;

  const setDiscountFromCategory = React.useCallback((category, nextValues = {}) => {
    if (category === 'NONE') {
      onDiscountChange?.('—');
      return;
    }
    if (category === 'PERCENT') {
      const value = String(nextValues.percentOff ?? percentOff).trim();
      onDiscountChange?.(value ? `${value}% off` : '');
      return;
    }
    if (category === 'FLAT') {
      const value = String(nextValues.flatOff ?? flatOff).trim();
      onDiscountChange?.(value ? `Flat Rs ${value} off` : '');
      return;
    }
    if (category === 'BUY_GET') {
      const buy = String(nextValues.buyQty ?? buyQty).trim();
      const get = String(nextValues.getQty ?? getQty).trim();
      onDiscountChange?.(buy && get ? `Buy ${buy} Get ${get}` : '');
      return;
    }
    if (category === 'BILL_VALUE') {
      const spend = String(nextValues.spendAmount ?? spendAmount).trim();
      const get = String(nextValues.getAmount ?? getAmount).trim();
      onDiscountChange?.(spend && get ? `Spend Rs ${spend}, get Rs ${get} off` : '');
    }
  }, [buyQty, flatOff, getAmount, getQty, onDiscountChange, percentOff, spendAmount]);

  // Unified mechanic: simple types compose the `discount` label; advanced types
  // (tiered / flat-price / free-item) drive the structured `advanced` fields.
  const ADVANCED_MECHANICS = ['TIERED', 'FLAT_PRICE', 'FREE_ITEM'];
  const advancedRules = advanced || {};
  const tiers = Array.isArray(advancedRules.tiers) ? advancedRules.tiers : [];
  const setTier = (i, key, val) =>
    onAdvancedChange?.({ tiers: tiers.map((t, idx) => (idx === i ? { ...t, [key]: val } : t)) });
  const addTier = () => onAdvancedChange?.({ tiers: [...tiers, { minSpend: '', type: 'PERCENT', value: '' }] });
  const removeTier = (i) => onAdvancedChange?.({ tiers: tiers.filter((_, idx) => idx !== i) });

  const handleMechanicChange = (next) => {
    setDiscountCategory(next);
    onAdvancedChange?.({ mechanicType: ADVANCED_MECHANICS.includes(next) ? next : null });
    if (next === 'PERCENT') {
      const v = percentOff || '10'; setPercentOff(v); setDiscountFromCategory('PERCENT', { percentOff: v });
    } else if (next === 'FLAT') {
      const v = flatOff || '200'; setFlatOff(v); setDiscountFromCategory('FLAT', { flatOff: v });
    } else if (next === 'BUY_GET') {
      const b = buyQty || '2'; const g = getQty || '1'; setBuyQty(b); setGetQty(g);
      setDiscountFromCategory('BUY_GET', { buyQty: b, getQty: g });
    } else if (next === 'BILL_VALUE') {
      const s = spendAmount || '1500'; const g = getAmount || '200'; setSpendAmount(s); setGetAmount(g);
      setDiscountFromCategory('BILL_VALUE', { spendAmount: s, getAmount: g });
    } else if (next === 'TIERED') {
      if (!tiers.length) onAdvancedChange?.({ tiers: [{ minSpend: '', type: 'PERCENT', value: '' }] });
      onDiscountChange?.('Tiered discount');
    } else if (next === 'FLAT_PRICE') {
      onDiscountChange?.(advancedRules.flatPriceValue ? `Now Rs ${advancedRules.flatPriceValue}` : 'Flat price');
    } else if (next === 'FREE_ITEM') {
      onDiscountChange?.(String(advancedRules.freeItemLabel || '').trim() ? `Free ${advancedRules.freeItemLabel}` : 'Free add-on');
    } else {
      setDiscountFromCategory('NONE');
    }
  };

  // --- Seed local discount state from the incoming `discount` prop (mount-only) ---
  // A ref prevents re-entrance: once the component itself has updated the discount
  // via setDiscountFromCategory, we must NOT re-parse it (which would loop).
  const hasSyncedDiscount = React.useRef(false);

  React.useEffect(() => {
    // Only parse the prop once on mount (or when the modal first opens with a value).
    if (hasSyncedDiscount.current) {
      return;
    }
    // Advanced mechanic (tiered / flat-price / free-item) is identified by the
    // structured field, not the label — seed the unified selector from it.
    const advMech = (advanced || {}).mechanicType;
    if (advMech === 'TIERED' || advMech === 'FLAT_PRICE' || advMech === 'FREE_ITEM') {
      hasSyncedDiscount.current = true;
      setDiscountCategory(advMech);
      return;
    }
    const text = String(discount || '').trim();
    if (!text) {
      return;
    }
    hasSyncedDiscount.current = true;

    if (text === '—') {
      setDiscountCategory('NONE');
      return;
    }
    const percentMatch = text.match(/^(\d{1,2})\s*%\s*off$/i);
    if (percentMatch) {
      setDiscountCategory('PERCENT');
      setPercentOff(percentMatch[1]);
      return;
    }
    const flatMatch = text.match(/^Flat\s+Rs\s+(\d+)\s+off$/i);
    if (flatMatch) {
      setDiscountCategory('FLAT');
      setFlatOff(flatMatch[1]);
      return;
    }
    const buyGetMatch = text.match(/^Buy\s+(\d+)\s+Get\s+(\d+)$/i);
    if (buyGetMatch) {
      setDiscountCategory('BUY_GET');
      setBuyQty(buyGetMatch[1]);
      setGetQty(buyGetMatch[2]);
      return;
    }
    const billMatch = text.match(/^Spend\s+Rs\s+(\d+),\s*get\s+Rs\s+(\d+)\s+off$/i);
    if (billMatch) {
      setDiscountCategory('BILL_VALUE');
      setSpendAmount(billMatch[1]);
      setGetAmount(billMatch[2]);
    }
  }, [discount, advanced]);

  const previewConfig = {
    title,
    discountCategory, percentOff, flatOff, buyQty, getQty, spendAmount, getAmount,
    appliesOn, appliesCategories, minCartValue,
    audience, validDaysLabel, validityFrom, validityUntil,
    maxDiscountCap, redemptionLimitType, redemptionLimitValue, couponCode,
    advanced,
  };

  return (
    <section className="offers-composer campaign-builder-shell">
      <div className="offers-composer-header">
        <div>
          <h4>Campaign Builder</h4>
        </div>
        <p
          className="offers-composer-note"
          title={[...selectedChannels].map((channel) => channelLabels[channel]).filter(Boolean).join(', ')}
        >
          Selected channels: {selectedChannelsLabel}
        </p>
      </div>

      <div className="offers-builder-split">
        <div className="offers-builder-col">
      <nav className="offers-stepper" aria-label="Campaign steps">
        {STEPS.map((label, index) => (
          <button
            key={label}
            type="button"
            className={`offers-stepper__item ${index === step ? 'is-active' : ''} ${index < step ? 'is-done' : ''}`}
            onClick={() => setStep(index)}
          >
            <span className="offers-stepper__num">{index < step ? '✓' : index + 1}</span>
            <span className="offers-stepper__label">{label}</span>
          </button>
        ))}
      </nav>

      <div className="offers-form-grid">
        {step === 0 && (
        <>
        {/* CP-111 (S3): the Spotlight/Discover visibility picker was removed —
            placement is computed per customer by the scored feed. */}
        <div className="offers-field full-width offers-channel-highlight">
          <label>Placement Channels</label>
          <p className="offers-field-helper">Choose where customers can see this campaign across Home Feed, In-Store, and Active Queue surfaces.</p>
          <div className="offers-chip-group">
            {channelOptions.map((channel) => (
              <button
                key={channel}
                type="button"
                className={`offers-chip ${selectedChannels.has(channel) ? 'active info' : ''}`}
                onClick={() => toggleChannel(channel)}
              >
                {channelLabels[channel]}
              </button>
            ))}
          </div>
        </div>

        <div className="offers-field">
          <label>Campaign Title</label>
          <select
            className="analytics-select"
            value={title}
            onChange={(event) => onTitleChange?.(event.target.value)}
          >
            <option value="">Select a title</option>
            {titleGroups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        </>
        )}

        {step === 1 && (
        <>
        <div className="offers-field">
          <label>Offer mechanic</label>
          <p className="offers-field-helper">What the customer gets. The live preview on the right explains the effect.</p>
          <div className="offers-discount-shell">
            <select
              className="analytics-select"
              value={discountCategory}
              onChange={(event) => handleMechanicChange(event.target.value)}
            >
              <option value="NONE">No discount</option>
              <option value="PERCENT">Percentage off</option>
              <option value="FLAT">Flat amount off</option>
              <option value="BUY_GET">Buy X, get Y free</option>
              <option value="BILL_VALUE">Spend &amp; save</option>
              <option value="TIERED">Tiered (spend more, save more)</option>
              <option value="FLAT_PRICE">Flat price (now ₹X)</option>
              <option value="FREE_ITEM">Free add-on / service</option>
            </select>

            {discountCategory === 'PERCENT' && (
              <div className="offers-discount-row" aria-label="Percentage discount input">
                <input
                  className="analytics-select offers-discount-input"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  max="95"
                  value={percentOff}
                  onChange={(event) => {
                    const next = event.target.value;
                    setPercentOff(next);
                    setDiscountFromCategory('PERCENT', { percentOff: next });
                  }}
                />
                <span className="offers-discount-suffix">% off</span>
              </div>
            )}

            {discountCategory === 'FLAT' && (
              <div className="offers-discount-row" aria-label="Flat discount input">
                <span className="offers-discount-prefix">Flat Rs</span>
                <input
                  className="analytics-select offers-discount-input"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  value={flatOff}
                  onChange={(event) => {
                    const next = event.target.value;
                    setFlatOff(next);
                    setDiscountFromCategory('FLAT', { flatOff: next });
                  }}
                />
                <span className="offers-discount-suffix">off</span>
              </div>
            )}

            {discountCategory === 'BUY_GET' && (
              <div className="offers-discount-row" aria-label="Buy and get input">
                <span className="offers-discount-prefix">Buy</span>
                <input
                  className="analytics-select offers-discount-input"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  value={buyQty}
                  onChange={(event) => {
                    const next = event.target.value;
                    setBuyQty(next);
                    setDiscountFromCategory('BUY_GET', { buyQty: next });
                  }}
                />
                <span className="offers-discount-prefix">Get</span>
                <input
                  className="analytics-select offers-discount-input"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  value={getQty}
                  onChange={(event) => {
                    const next = event.target.value;
                    setGetQty(next);
                    setDiscountFromCategory('BUY_GET', { getQty: next });
                  }}
                />
              </div>
            )}

            {discountCategory === 'BILL_VALUE' && (
              <div className="offers-discount-row nowrap" aria-label="Bill value offer input">
                <span className="offers-discount-prefix">Spend Rs</span>
                <input
                  className="analytics-select offers-discount-input"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  value={spendAmount}
                  onChange={(event) => {
                    const next = event.target.value;
                    setSpendAmount(next);
                    setDiscountFromCategory('BILL_VALUE', { spendAmount: next });
                  }}
                />
                <span className="offers-discount-prefix">Get Rs</span>
                <input
                  className="analytics-select offers-discount-input"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  value={getAmount}
                  onChange={(event) => {
                    const next = event.target.value;
                    setGetAmount(next);
                    setDiscountFromCategory('BILL_VALUE', { getAmount: next });
                  }}
                />
                <span className="offers-discount-suffix">off</span>
              </div>
            )}

            {discountCategory === 'TIERED' && (
              <div className="offers-mechanic-block" aria-label="Tiered discount input">
                {tiers.map((t, idx) => (
                  <div key={idx} className="offers-discount-row" style={{ flexWrap: 'wrap', gap: 6 }}>
                    <span className="offers-discount-prefix">Spend ₹</span>
                    <input className="analytics-select offers-discount-input" type="number" min="0"
                      value={t.minSpend ?? ''} onChange={(e) => setTier(idx, 'minSpend', e.target.value)} />
                    <select className="analytics-select" value={t.type || 'PERCENT'}
                      onChange={(e) => setTier(idx, 'type', e.target.value)}>
                      <option value="PERCENT">% off</option>
                      <option value="FLAT">₹ off</option>
                    </select>
                    <input className="analytics-select offers-discount-input" type="number" min="0"
                      value={t.value ?? ''} onChange={(e) => setTier(idx, 'value', e.target.value)} />
                    <button type="button" className="btn ghost" onClick={() => removeTier(idx)}>Remove</button>
                  </div>
                ))}
                <button type="button" className="btn ghost" onClick={addTier}>+ Add tier</button>
              </div>
            )}

            {discountCategory === 'FLAT_PRICE' && (
              <div className="offers-discount-row" aria-label="Flat price input">
                <span className="offers-discount-prefix">Now ₹</span>
                <input className="analytics-select offers-discount-input" type="number" min="0"
                  value={advancedRules.flatPriceValue ?? ''}
                  onChange={(e) => {
                    onAdvancedChange?.({ flatPriceValue: e.target.value });
                    onDiscountChange?.(e.target.value ? `Now Rs ${e.target.value}` : 'Flat price');
                  }} />
              </div>
            )}

            {discountCategory === 'FREE_ITEM' && (
              <div className="offers-discount-row" aria-label="Free item input">
                <span className="offers-discount-prefix">Free</span>
                {hasServiceMenu ? (
                  <select className="analytics-select" value={advancedRules.freeItemLabel || ''}
                    onChange={(e) => {
                      onAdvancedChange?.({ freeItemLabel: e.target.value });
                      onDiscountChange?.(e.target.value ? `Free ${e.target.value}` : 'Free add-on');
                    }}>
                    <option value="">Select a service…</option>
                    {availableServiceNames.concat(selectedServiceNames).map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                ) : (
                  <input className="analytics-select offers-discount-input" type="text"
                    placeholder="e.g. Head massage" value={advancedRules.freeItemLabel || ''}
                    onChange={(e) => {
                      onAdvancedChange?.({ freeItemLabel: e.target.value });
                      onDiscountChange?.(e.target.value ? `Free ${e.target.value}` : 'Free add-on');
                    }} />
                )}
              </div>
            )}
          </div>
          {discount && discountCategory !== 'NONE' && (
            <p className="offers-field-helper" aria-live="polite">Selected: {discount}</p>
          )}
        </div>

        <div className="offers-field full-width">
          <label>Offer Applies On</label>
          <select
            className="analytics-select"
            value={appliesOn}
            onChange={(event) => {
              const next = event.target.value;
              onAppliesOnChange?.(next);
              if (next !== 'CATEGORIES') {
                onAppliesCategoriesChange?.('');
              }
              if (next !== 'MIN_CART') {
                onMinCartValueChange?.('');
              }
            }}
          >
            <option value="">Select where this offer applies</option>
            <option value="STORE_WIDE">Store-wide (all items)</option>
            <option value="CATEGORIES">Specific services</option>
            <option value="MIN_CART">Minimum cart value</option>
          </select>
          {appliesOn === 'CATEGORIES' && (
            <div className="offers-service-picker">
              <select
                className="analytics-select"
                value=""
                disabled={!hasServiceMenu || availableServiceNames.length === 0}
                onChange={(event) => {
                  addService(event.target.value);
                  event.target.value = '';
                }}
              >
                <option value="">
                  {isLoadingServices && !hasServiceMenu
                    ? 'Loading your service menu…'
                    : !hasServiceMenu
                      ? 'No services in your service menu'
                      : availableServiceNames.length === 0
                        ? 'All services added'
                        : 'Add a service…'}
                </option>
                {availableServiceNames.map((serviceName) => (
                  <option key={serviceName} value={serviceName}>{serviceName}</option>
                ))}
              </select>
              {selectedServiceNames.length > 0 && (
                <div className="offers-chip-group">
                  {selectedServiceNames.map((serviceName) => (
                    <span key={serviceName} className="offers-chip active offers-service-chip">
                      {serviceName}
                      <button
                        type="button"
                        className="offers-service-chip-remove"
                        aria-label={`Remove ${serviceName}`}
                        onClick={() => removeService(serviceName)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {appliesOn === 'MIN_CART' && (
            <div className="offers-discount-row" aria-label="Minimum cart value input">
              <span className="offers-discount-prefix">Rs</span>
              <input
                className="analytics-select offers-discount-input"
                type="number"
                inputMode="numeric"
                min="1"
                placeholder="1500"
                value={minCartValue}
                onChange={(event) => onMinCartValueChange?.(event.target.value)}
              />
              <span className="offers-discount-suffix">minimum cart value</span>
            </div>
          )}
        </div>

        <p className="offers-step-divider">Limits &amp; safety</p>

        <div className="offers-field">
          <label>Redemption Limit</label>
          <div className="offers-discount-shell">
            <select
              className="analytics-select"
              value={normalizedLimitType}
              onChange={(event) => {
                const next = event.target.value;
                onRedemptionLimitTypeChange?.(next);
                if (next === 'none') {
                  onRedemptionLimitValueChange?.('');
                } else if (!String(redemptionLimitValue || '').trim()) {
                  onRedemptionLimitValueChange?.('100');
                }
              }}
            >
              <option value="none">No limit</option>
              <option value="per_user">Per customer cap</option>
              <option value="global">Total campaign cap</option>
            </select>

            {requiresRedemptionValue && (
              <div className="offers-discount-row" aria-label="Redemption cap value">
                <input
                  className="analytics-select offers-discount-input"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  value={normalizedLimitValue}
                  onChange={(event) => onRedemptionLimitValueChange?.(event.target.value)}
                />
                <span className="offers-discount-suffix">
                  {normalizedLimitType === 'per_user' ? 'redemptions per customer' : 'total redemptions'}
                </span>
              </div>
            )}
          </div>
          {requiresRedemptionValue && !redemptionValueOk && (
            <p className="offers-field-helper">Enter a number greater than 0.</p>
          )}
        </div>

        <div className="offers-field">
          <label>Max Discount Cap</label>
          <div className="offers-discount-row" aria-label="Maximum discount cap input">
            <span className="offers-discount-prefix">Rs</span>
            <input
              className="analytics-select offers-discount-input"
              type="number"
              inputMode="numeric"
              min="1"
              placeholder="Optional"
              value={maxDiscountCap || ''}
              onChange={(event) => onMaxDiscountCapChange?.(event.target.value)}
            />
            <span className="offers-discount-suffix">max off</span>
          </div>
          <p className="offers-field-helper">Caps how much any single customer can save. Leave blank for no cap.</p>
        </div>
        </>
        )}

        {step === 2 && (
        <>
        <div className="offers-field">
          <label>Valid From</label>
          <div className="offers-datetime-row">
            <input
              className="analytics-select"
              type="date"
              min={minFromDate}
              max={maxDateValue}
              value={fromParts.date}
              onChange={(event) => {
                const nextDate = event.target.value;
                if (!nextDate) {
                  onValidityFromChange?.('');
                  return;
                }
                const currentTime = fromParts.time || '';
                const nextTime = nextDate === todayValue
                  ? (currentTime && currentTime >= defaultFromTime ? currentTime : defaultFromTime)
                  : (currentTime || defaultFromTime);
                onValidityFromChange?.(composeIsoLocal(nextDate, nextTime));
              }}
            />
            <div className="offers-time-input-wrap">
              <input
                className="time-input offers-time-input"
                type="time"
                step={900}
                min={minFromTime}
                value={fromParts.time}
                disabled={!fromParts.date}
                onChange={(event) => {
                  const nextTime = event.target.value;
                  if (!fromParts.date) {
                    return;
                  }
                  if (fromParts.date === todayValue && nextTime < defaultFromTime) {
                    onValidityFromChange?.(composeIsoLocal(fromParts.date, defaultFromTime));
                    return;
                  }
                  onValidityFromChange?.(composeIsoLocal(fromParts.date, nextTime));
                }}
              />
            </div>
          </div>
        </div>
        <div className="offers-field">
          <label>Valid Until</label>
          <div className="offers-datetime-row">
            <input
              className="analytics-select"
              type="date"
              min={minUntilDate}
              max={maxDateValue}
              value={untilParts.date}
              onChange={(event) => {
                const nextDate = event.target.value;
                if (!nextDate) {
                  onValidityUntilChange?.('');
                  return;
                }
                const currentTime = untilParts.time || '';
                const minTimeForDate = nextDate === fromParts.date && fromParts.time
                  ? (addMinutesToTime(fromParts.time, 15) || fromParts.time)
                  : (nextDate === todayValue ? defaultFromTime : '00:00');
                const nextTime = currentTime && currentTime >= minTimeForDate
                  ? currentTime
                  : (nextDate === todayValue ? defaultUntilTime : (minTimeForDate || defaultUntilTime));
                const nextIso = composeIsoLocal(nextDate, nextTime);
                if (minUntilIso && compareIsoLocal(nextIso, minUntilIso) <= 0) {
                  onValidityUntilChange?.('');
                  return;
                }
                if (!minUntilIso && nextDate === todayValue && nextTime < defaultFromTime) {
                  onValidityUntilChange?.('');
                  return;
                }
                onValidityUntilChange?.(nextIso);
              }}
            />
            <div className="offers-time-input-wrap">
              <input
                className="time-input offers-time-input"
                type="time"
                step={900}
                min={minUntilTime}
                value={untilParts.time}
                disabled={!untilParts.date}
                onChange={(event) => {
                  const nextTime = event.target.value;
                  if (!untilParts.date) {
                    return;
                  }
                  const nextIso = composeIsoLocal(untilParts.date, nextTime);
                  if (minUntilIso && compareIsoLocal(nextIso, minUntilIso) <= 0) {
                    const requiredMinTime = addMinutesToTime(fromParts.time, 15);
                    if (requiredMinTime) {
                      onValidityUntilChange?.(composeIsoLocal(untilParts.date, requiredMinTime));
                    } else {
                      onValidityUntilChange?.('');
                    }
                    return;
                  }
                  if (!minUntilIso && untilParts.date === todayValue && nextTime < defaultFromTime) {
                    onValidityUntilChange?.(composeIsoLocal(untilParts.date, defaultUntilTime));
                    return;
                  }
                  onValidityUntilChange?.(nextIso);
                }}
              />
            </div>
          </div>
        </div>

        <div className="offers-field">
          <label>Audience Segment</label>
          <select
            className="analytics-select"
            value={audience || 'all'}
            onChange={(event) => onAudienceChange?.(event.target.value)}
          >
            <option value="all">All customers</option>
            <option value="new">New customers only</option>
            <option value="repeat">Repeat customers only</option>
            <option value="vip">VIP / high spenders</option>
          </select>
        </div>

        <div className="offers-field">
          <label>Valid Days</label>
          <p className="offers-field-helper">Leave all unselected to run every day of the week.</p>
          <div className="offers-chip-group">
            {DAY_OPTIONS.map((day) => (
              <button
                key={day}
                type="button"
                className={`offers-chip ${selectedValidDays.includes(day) ? 'active' : ''}`}
                onClick={() => toggleValidDay(day)}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
        </>
        )}

        {step === 3 && (
        <>
        <div className="offers-field">
          <label>Coupon Code</label>
          <div className="offers-coupon-row">
            <input
              className="analytics-select offers-coupon-input"
              type="text"
              readOnly
              placeholder="Click Generate to create a code"
              value={couponCode || ''}
            />
            <button
              className="btn ghost offers-coupon-generate"
              type="button"
              onClick={generateCouponCode}
            >
              {couponCode ? 'Regenerate' : 'Generate'}
            </button>
          </div>
          <p className="offers-field-helper">Codes are generated for you — customers copy this code to redeem in store.</p>
        </div>

        <div className="offers-field full-width">
          <label>Terms &amp; Conditions</label>
          <div className="offers-discount-row nowrap">
            <input
              className="analytics-select"
              type="text"
              placeholder="Add a term, e.g. Cannot be combined with other offers"
              value={newTerm}
              onChange={(event) => setNewTerm(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addTerm();
                }
              }}
            />
            <button
              className="btn ghost"
              type="button"
              onClick={addTerm}
              disabled={!String(newTerm || '').trim()}
            >
              Add
            </button>
          </div>
          {termsList.length > 0 && (
            <ul className="offers-terms-list">
              {termsList.map((term, index) => (
                <li key={`${term}-${index}`} className="offers-terms-item">
                  <span>{term}</span>
                  <button
                    className="offers-terms-remove"
                    type="button"
                    aria-label={`Remove term: ${term}`}
                    onClick={() => removeTerm(index)}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="offers-field-helper">Add each term one at a time — every term shows as a separate bullet in the customer offer details.</p>
        </div>
        </>
        )}
      </div>

      {step === 1 && (
        <AdvancedCampaignRules advanced={advanced} onChange={onAdvancedChange} section="limits" />
      )}
      {step === 2 && (
        <AdvancedCampaignRules advanced={advanced} onChange={onAdvancedChange} section="when" />
      )}

      <div className="offers-composer-actions">
        <button
          className="btn ghost"
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
        >
          Back
        </button>
        {step < LAST_STEP ? (
          <button className="btn primary" type="button" onClick={() => setStep((s) => Math.min(LAST_STEP, s + 1))}>
            Next: {STEPS[step + 1]}
          </button>
        ) : (
          <>
            <button className="btn ghost" type="button" onClick={onSaveDraft} disabled={!onSaveDraft || !canSubmit}>
              Save draft
            </button>
            <button className="btn primary" type="button" onClick={onPublish} disabled={!onPublish || !canSubmit}>
              Publish campaign
            </button>
          </>
        )}
      </div>
        </div>
        <OfferLivePreview config={previewConfig} />
      </div>
    </section>
  );
};

export default CampaignBuilder;
